<template>
  <div>
    <search-esim-inventory @submit="submitSearch" />

    <div class="simiot-table-operation-buttons">
      <a-space>
        <a-button
          type="primary"
          @click="showImportModal"
          v-if="isShowMenu('esim_inventories/import')"
        >入库</a-button>

        <a-button
          type="primary"
          @click="showDestroyModal"
          v-if="isShowMenu('esim_inventories/delete')"
        >删除eSIM</a-button>

        <a-button type="primary" @click="exportData">导出</a-button>
      </a-space>
    </div>

    <a-table
      size="middle"
      :scroll="{ x: 'max-content' }"
      :columns="columns"
      :data-source="data"
      :loading="loading"
      :pagination="false"
      :row-selection="{ selectedRowKeys: selectedRowKeys, onChange: onSelectChange }"
      row-key="id"
    />

    <pagination
      v-show="pagination.total_count > 0"
      :total-count="pagination.total_count"
      :page.sync="query.page"
      :per-page.sync="query.per_page"
      @change="fetchData"
    />

    <import-esim-inventory
      v-if="isShowImportModal"
      :visible.sync="isShowImportModal"
      @completed="fetchData"
    />

    <destroy-esim-inventory
      v-if="isShowDestroyModal"
      :visible.sync="isShowDestroyModal"
      :eids="selectedEids"
      @completed="fetchData"
    />
  </div>
</template>

<script>
import { hasPermission } from '@/utils/permission'
import { findEsimInventories, exportEsimInventories } from '@/api/esim_inventory'
import Pagination from '@/components/Pagination'
import SearchEsimInventory from '@/views/esim_inventories/Search'
import ImportEsimInventory from '@/views/esim_inventories/import'
import { formatFloat } from '@/utils/filter'
import { exportExcel } from '@/api/excel'

export default {
  name: 'EsimInventoryList',
  components: {
    Pagination,
    SearchEsimInventory,
    ImportEsimInventory,
    DestroyEsimInventory: () => import('@/views/batch_operations/DestroyEsimInventory')
  },
  data() {
    return {
      data: [],
      loading: true,
      isShowImportModal: false,
      isShowDestroyModal: false,
      selectedRowKeys: [],
      pagination: {
        total_count: 0
      },
      query: {}
    }
  },
  computed: {
    columns() {
      return [
        {
          title: 'EID',
          dataIndex: 'eid',
          width: 300,
          fixed: 'left',
          customCell: () => ({ style: { whiteSpace: 'nowrap' }})
        },
        { title: 'eSIM供应商', dataIndex: 'esim_supplier_name', width: 140 },
        { title: 'SGP规范', dataIndex: 'sgp_spec_names', width: 160 },
        { title: 'LPA/IPA', dataIndex: 'lpa_ipa_name', width: 120 },
        {
          title: '是否有预制号',
          dataIndex: 'has_preconfigured_number',
          width: 120,
          customRender: (t) => (t ? '是' : '否')
        },
        {
          title: '预制号Fallback',
          dataIndex: 'is_preconfigured_number_fallback',
          width: 130,
          customRender: (t) => (t ? '是' : '否')
        },
        {
          title: '预制默认SM-DP+',
          dataIndex: 'has_preconfigured_default_smdp',
          width: 140,
          customRender: (t) => (t ? '是' : '否')
        },
        { title: 'SM-DP+地址', dataIndex: 'smdp_address', width: 160, ellipsis: true },
        {
          title: '预制根SM-DS',
          dataIndex: 'has_preconfigured_root_smds',
          width: 120,
          customRender: (t) => (t ? '是' : '否')
        },
        { title: 'SM-DS地址', dataIndex: 'smds_address', width: 160, ellipsis: true },
        { title: 'eIM平台', dataIndex: 'eim_platform_name', width: 120 },
        { title: 'eIM注册状态', dataIndex: 'eim_register_status_name', width: 120 },
        { title: '通信协议', dataIndex: 'eim_support_protocol_name', width: 100 },
        { title: '请求间隔(秒)', dataIndex: 'eim_request_interval', width: 120 },
        { title: '卡片材质', dataIndex: 'card_type_name', width: 120 },
        { title: '卡片尺寸', dataIndex: 'card_shape_name', width: 120 },
        { title: '生产批次', dataIndex: 'production_batch', width: 160 },
        { title: '生产日期', dataIndex: 'production_date', width: 120 },
        { title: '库房位置', dataIndex: 'warehouse_location_name', width: 140 },
        {
          title: '制卡费(元)',
          dataIndex: 'card_fee',
          width: 110,
          customRender: (t) => formatFloat(t)
        },
        { title: '备注', dataIndex: 'remark', width: 160, ellipsis: true },
        { title: '入库时间', dataIndex: 'created_at', width: 170 }
      ]
    },
    selectedEids() {
      const idSet = new Set(this.selectedRowKeys)
      return this.data
        .filter((row) => idSet.has(row.id))
        .map((row) => row.eid)
        .filter((eid) => eid)
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    isShowMenu(menu) {
      return hasPermission(menu)
    },

    onSelectChange(selectedRowKeys) {
      this.selectedRowKeys = selectedRowKeys
    },

    showImportModal() {
      this.isShowImportModal = true
    },

    showDestroyModal() {
      this.isShowDestroyModal = true
    },

    submitSearch(search) {
      this.query = Object.assign({},
        this.$options.data().query,
        { per_page: this.query.per_page },
        search)
      this.fetchData()
    },

    fetchData() {
      this.loading = true
      this.selectedRowKeys = []
      findEsimInventories(this.query).then((res) => {
        this.data = res.data
        this.pagination = res.pagination
        this.loading = false
      })
    },

    buildExportParams() {
      const params = Object.assign({}, this.query)
      if (this.selectedRowKeys.length > 0) {
        params.ids = this.selectedRowKeys.slice()
      }
      return params
    },

    exportData() {
      exportEsimInventories(this.buildExportParams()).then((res) => {
        if (res.code === 0 && res.data.is_direct_download) {
          exportExcel(res.data.id, res.data.file_name)
        }
      })
    }
  }
}
</script>
